import { HttpLink, InMemoryCacheConfig } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { sha256 } from 'crypto-hash'
import { type Dispatcher } from 'undici' // Import type only, this is an isomorphic file importing something from a server only module.
import Cookies from 'universal-cookie'

import { ServiceFilterPairs, catalogCacheConfig } from '@syconium/weeping-figs'

import catalogSupergraphCodegenResults from '../../__generated__/graphql/catalog/graphql'

import { cookieKeys } from './Cookies.config'
import { headerKeys } from './Headers.config'
import { LocalizationAttributes } from './Localization.config'
import { PreviewDirectives } from './PreviewDirectives.config'

export const magnoliaSupergraphGraphqlCacheConfig: InMemoryCacheConfig = {
  ...catalogCacheConfig,
  possibleTypes: catalogSupergraphCodegenResults.possibleTypes,
}

const supergraphBaseUrl =
  typeof window === 'undefined' && process.env.NEXT_PUBLIC_SERVER_API_BASE_URL
    ? process.env.NEXT_PUBLIC_SERVER_API_BASE_URL
    : process.env.NEXT_PUBLIC_CLIENT_API_BASE_URL
const shopName = process.env.NEXT_PUBLIC_SHOPIFY_SHOP_NAME

const getAuthLink = (authToken?: string) => {
  return setContext((_operation, { headers }) => {
    const clientSideCookies = new Cookies()
    const authorization =
      clientSideCookies.get(cookieKeys.authToken.key) ?? authToken ?? headers?.authorization
    return {
      headers: {
        ...(headers ?? {}),
        ...(authorization ? { authorization } : {}),
      },
    }
  })
}

const getLocalizationLink = (localization: LocalizationAttributes) => {
  return setContext((_operation, { headers }) => {
    const clientSideCookies = new Cookies()
    return {
      headers: {
        ...(headers ?? {}),
        [headerKeys.region]: clientSideCookies.get(cookieKeys.region.key) ?? localization.region.id,
        [headerKeys.locale]: clientSideCookies.get(cookieKeys.locale.key) ?? localization.locale,
        [headerKeys.currency]:
          clientSideCookies.get(cookieKeys.currency.key) ?? localization.currency,
        ...(shopName && {
          [headerKeys.shopName]: shopName,
        }),
      },
    }
  })
}

const getCustomerTierLink = () => {
  return setContext((_operation, _previousContext) => {
    return { customerTier: 'web' }
  })
}

const getPreviewDirectivesLink = (previewDirectives: PreviewDirectives) => {
  return setContext(
    (
      _operation,
      { isPreview: alreadyIsPreview, includeUnavailable: alreadyIncludeUnavailable, headers }
    ) => {
      const includeUnavailable =
        alreadyIncludeUnavailable ?? previewDirectives.includeUnavailableProducts
      const isPreview = alreadyIsPreview ?? previewDirectives.includeUnpublishedCmsContent
      const launchTags = previewDirectives.includeUnpublishedProductsForLaunchTags
      const serviceFilters = ServiceFilterPairs.buildServiceFilterHeader({
        includeUnavailable,
        isPreview,
        launchTags,
        customerTier: 'web',
      })
      return {
        includeUnavailable,
        isPreview,
        launchTags,
        headers: {
          ...(headers ?? {}),
          ...(serviceFilters ? { [headerKeys.serviceFilters]: serviceFilters } : {}),
        },
      }
    }
  )
}

const getPersistedQueryLink = () => {
  return createPersistedQueryLink({ sha256, useGETForHashedQueries: true })
}

const getHttpLink = (graphPathname: string, ssrFetchAgent?: Dispatcher) => {
  return new HttpLink({
    uri: `${supergraphBaseUrl}${graphPathname}`,
    fetchOptions: ssrFetchAgent
      ? {
          dispatcher: ssrFetchAgent,
        }
      : undefined,
  })
}

export const getGraphqlClientLinks = ({
  includeAuth,
  authToken,
  graphPathname,
  localization,
  previewDirectives,
  ssrFetchAgent,
}: {
  includeAuth: boolean
  authToken: string | undefined
  graphPathname: string
  localization: LocalizationAttributes
  previewDirectives: PreviewDirectives
  ssrFetchAgent?: Dispatcher
}) => {
  const links = includeAuth
    ? getAuthLink(authToken).concat(getLocalizationLink(localization))
    : getLocalizationLink(localization)
  return links
    .concat(getCustomerTierLink())
    .concat(getPreviewDirectivesLink(previewDirectives))
    .concat(getPersistedQueryLink())
    .concat(getHttpLink(graphPathname, ssrFetchAgent))
}

export const graphqlClientsDefaultOptions = {
  query: {
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
  },
} as const

export type GraphQlClients = 'cache-optimized' | 'auth-supported'
